/**
 * RL-020 — client-side resolver for automatic session-timeout configuration.
 *
 * Reads the staff-visible tenant settings (which include the `session` block and
 * operating hours), resolves the effective timeout for the current staff member,
 * and answers the shift-aware gate ("is now inside operating hours?").
 */
import { api } from './api'
import { getCurrentTenant } from './tenant'

export type PosType = 'reception' | 'reservation' | 'therapy' | 'restaurant' | 'shop'

export interface SessionTimeoutConfig {
  enabled: boolean
  timeoutMinutes: number
  warningSeconds: number
  shiftAware: boolean
  posTypeOverrides: Partial<Record<PosType, number>>
}

export interface ResolvedSessionConfig extends SessionTimeoutConfig {
  operatingHoursStart: string // "HH:MM" in tenant timezone
  operatingHoursEnd: string
  timezone: string
}

export const SESSION_DEFAULTS: SessionTimeoutConfig = {
  enabled: true,
  timeoutMinutes: 10,
  warningSeconds: 120,
  // Feedback requirement: timeout applies at all times — keep in sync with the
  // backend SESSION_TIMEOUT_DEFAULTS.shiftAware (single behaviour, both sides).
  shiftAware: false,
  posTypeOverrides: {},
}

const DEFAULT_OPERATING_START = '08:00'
const DEFAULT_OPERATING_END = '20:00'
const DEFAULT_TZ = 'Europe/London'

/** Safe fallback used when the config cannot be loaded — fail CLOSED (keep the guard on). */
function defaultResolvedConfig(): ResolvedSessionConfig {
  return {
    ...SESSION_DEFAULTS,
    operatingHoursStart: DEFAULT_OPERATING_START,
    operatingHoursEnd: DEFAULT_OPERATING_END,
    timezone: DEFAULT_TZ,
  }
}

/** Clamp each per-POS override to the valid 5–30 range (defence-in-depth; backend also clamps). */
function clampOverrides(raw: unknown): Partial<Record<PosType, number>> {
  const out: Partial<Record<PosType, number>> = {}
  if (raw && typeof raw === 'object') {
    for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
      if (val !== undefined && val !== null) out[key as PosType] = clampMinutes(val)
    }
  }
  return out
}

// Short-TTL cache + in-flight dedupe: the config is refetched on window focus /
// visibility changes, which can fire in rapid bursts (e.g. switching to DevTools
// and back). Without this every burst hits /tenants/public/settings again.
// The cache is keyed by the current tenant subdomain, so a tenant switch (even
// without a full reload) bypasses the stale entry and refetches immediately.
const SESSION_CONFIG_TTL = 30_000
let sessionConfigCache: { tenant: string; config: ResolvedSessionConfig; timestamp: number } | null = null
let sessionConfigInflight: { tenant: string; promise: Promise<ResolvedSessionConfig> } | null = null

/**
 * Fetch the resolved session config from the public tenant-settings endpoint.
 * (The `session` block + operating hours are non-sensitive; the public endpoint is
 * unauthenticated and reliable — the authenticated /tenants routes are admin-gated.)
 *
 * RL-020 #3 (security): this is an enforcement feature, so it FAILS CLOSED — if the
 * config cannot be loaded we return safe defaults (timeout stays enabled) rather than
 * silently disabling the timeout.
 */
export async function fetchSessionConfig(): Promise<ResolvedSessionConfig> {
  const tenant = getCurrentTenant()?.toLowerCase() ?? ''

  if (sessionConfigCache && sessionConfigCache.tenant === tenant &&
      Date.now() - sessionConfigCache.timestamp < SESSION_CONFIG_TTL) {
    return sessionConfigCache.config
  }
  if (sessionConfigInflight && sessionConfigInflight.tenant === tenant) {
    return sessionConfigInflight.promise
  }

  const promise = fetchSessionConfigUncached(tenant)
  sessionConfigInflight = { tenant, promise }
  try {
    const config = await promise
    sessionConfigCache = { tenant, config, timestamp: Date.now() }
    return config
  } finally {
    sessionConfigInflight = null
  }
}

async function fetchSessionConfigUncached(tenant: string): Promise<ResolvedSessionConfig> {
  try {
    const res = await api.publicRequest(
      `/tenants/public/settings?subdomain=${encodeURIComponent(tenant)}`,
    )
    if (!res.ok) return defaultResolvedConfig()
    const json = await res.json()
    const settings = json?.data?.settings
    if (!settings) return defaultResolvedConfig()

    const s = settings.session ?? {}
    const booking = settings.booking ?? {}

    return {
      enabled: s.enabled ?? SESSION_DEFAULTS.enabled,
      timeoutMinutes: clampMinutes(s.timeoutMinutes ?? SESSION_DEFAULTS.timeoutMinutes),
      warningSeconds: typeof s.warningSeconds === 'number' ? s.warningSeconds : SESSION_DEFAULTS.warningSeconds,
      shiftAware: s.shiftAware ?? SESSION_DEFAULTS.shiftAware,
      posTypeOverrides: clampOverrides(s.posTypeOverrides),
      operatingHoursStart: booking.operatingHoursStart || DEFAULT_OPERATING_START,
      operatingHoursEnd: booking.operatingHoursEnd || DEFAULT_OPERATING_END,
      timezone: booking.timezone || settings.timezone || DEFAULT_TZ,
    }
  } catch {
    return defaultResolvedConfig()
  }
}

export function clampMinutes(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return SESSION_DEFAULTS.timeoutMinutes
  return Math.min(30, Math.max(5, Math.round(n)))
}

/**
 * RL-020 R11/AC9 (+ D2a): effective timeout for a staff member.
 * When several POS permissions carry overrides, the SHORTEST wins (most secure).
 * Falls back to the tenant-wide timeout when no override applies.
 */
export function resolveTimeoutMinutes(
  config: SessionTimeoutConfig,
  posPermissions: string[] | null | undefined,
): number {
  const overrides = config.posTypeOverrides ?? {}
  const applicable = (posPermissions ?? [])
    .map((p) => overrides[p as PosType])
    .filter((v): v is number => typeof v === 'number')
  return applicable.length > 0 ? Math.min(...applicable) : config.timeoutMinutes
}

function parseHHMM(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value?.trim() ?? '')
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

function nowMinutesInTimezone(timezone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date())
    let h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
    const min = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
    if (h === 24) h = 0 // some environments render midnight as "24"
    return h * 60 + min
  } catch {
    const d = new Date()
    return d.getHours() * 60 + d.getMinutes()
  }
}

/**
 * RL-020 R2/R3/R4 + AC1/AC2/AC3 shift gate (operating-hours proxy, decision D1):
 * true when the current tenant-local time falls INSIDE operating hours — in which
 * case the inactivity timeout must NOT trigger.
 */
export function isWithinOperatingHours(config: ResolvedSessionConfig, now: number = Date.now()): boolean {
  void now // tz-based; parameter kept for testability/signature symmetry
  const start = parseHHMM(config.operatingHoursStart)
  const end = parseHHMM(config.operatingHoursEnd)
  if (start === null || end === null) return false // misconfigured → treat as outside (safe: timeout can apply)
  if (start === end) return true // configured 24h open → always inside (never time out)

  const cur = nowMinutesInTimezone(config.timezone)
  if (end > start) {
    return cur >= start && cur < end
  }
  // Overnight window (e.g. 20:00–04:00): inside if after start OR before end
  return cur >= start || cur < end
}
